import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState({
    business_name: "",
    gst_number: "",
    default_hourly_rate: "",
    default_markup_percent: "",
    default_mileage_rate: "",
    address: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        if (r.data) setForm((f) => ({ ...f, ...r.data }));
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["default_hourly_rate", "default_markup_percent", "default_mileage_rate"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]);
      });
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stagger-in max-w-3xl">
      <div className="label-mono">/ SETTINGS</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">Your workshop</h1>
      <p className="text-zinc-400 mt-2">Defaults used across quotes, invoices and mileage.</p>

      <div className="mt-10 card-sharp p-8 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <Field
            label="Business name"
            value={form.business_name || ""}
            onChange={set("business_name")}
            testid="settings-business"
          />
          <Field
            label="GST number"
            value={form.gst_number || ""}
            onChange={set("gst_number")}
            testid="settings-gst-number"
          />
          <Field
            label="Default hourly rate (NZD)"
            type="number"
            value={form.default_hourly_rate ?? ""}
            onChange={set("default_hourly_rate")}
            testid="settings-hourly-rate"
          />
          <Field
            label="Default markup %"
            type="number"
            value={form.default_markup_percent ?? ""}
            onChange={set("default_markup_percent")}
            testid="settings-markup"
          />
          <Field
            label="Default mileage rate (per km)"
            type="number"
            value={form.default_mileage_rate ?? ""}
            onChange={set("default_mileage_rate")}
            testid="settings-mileage-rate"
          />
          <Field
            label="Phone"
            value={form.phone || ""}
            onChange={set("phone")}
            testid="settings-phone"
          />
          <div className="md:col-span-2">
            <Field
              label="Address"
              value={form.address || ""}
              onChange={set("address")}
              testid="settings-address"
            />
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="settings-save-btn"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        className="input-sharp"
        value={value}
        onChange={onChange}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
